/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { filter, has, includes, map } from 'lodash';
import { ColumnSortDirection, SortBy } from './types';
import { ZenFilterAbbreviated } from './components/filters/types';
import { downloadFile, filenameFromResponse } from '@/utils';
import { postRequest } from '@/utils/client/client';

type StringDict = { [key: string]: string };

export function convertAttributes(attrs: string[], attrsToFrom: StringDict): string[] {
  const mapIfIn = (attr: string, attrsToFrom: StringDict) => (has(attrsToFrom, attr) ? attrsToFrom[attr] : attr);

  return map(attrs, attr => mapIfIn(attr, attrsToFrom));
}

export function convertColumnSizes(columnSizes: object[], attrsToFrom: StringDict): object[] {
  return map(columnSizes, pref => {
    if (has(attrsToFrom, pref.name)) {
      return {
        name: attrsToFrom[pref.name],
        size: pref.size,
      };
    }

    return pref;
  });
}

export function convertSort(sort: SortBy, attrsToFrom: StringDict): SortBy {
  const mapIfIn = (attr: string, attrsToFrom: StringDict) => (has(attrsToFrom, attr) ? attrsToFrom[attr] : attr);

  return {
    by: mapIfIn(sort.by, attrsToFrom),
    direction: sort.direction,
  };
}

export function convertFilters(filters: ZenFilterAbbreviated[], attrsToFrom: StringDict): ZenFilterAbbreviated[] {
  return map(filters, (filter: ZenFilterAbbreviated) => {
    if (has(attrsToFrom, filter.f)) {
      return {
        f: attrsToFrom[filter.f],
        o: filter.o,
        v: filter.v,
      };
    }

    return filter;
  });
}

export function filterInvalidAttributes(attrs: string[], validAttrs: Set<string>): string[] {
  return filter(attrs, attr => validAttrs.has(attr));
}

export function filterInvalidColumnSizes(columnSizes: object[], validAttrs: Set<string>): object[] {
  return filter(columnSizes, pref => validAttrs.has(pref.name));
}

export function toDefaultSortIfInvalid(sort: SortBy, validAttrs: Set<string>, defaultSort: SortBy): SortBy {
  const isValidSortDirection = (direction: string): boolean => includes(Object.values(ColumnSortDirection), direction);
  const isValidSortAttribute = (field: string): boolean => validAttrs.has(field);
  const isValidSort = (sort: SortBy): boolean => isValidSortDirection(sort.direction) && isValidSortAttribute(sort.by);

  return isValidSort(sort) ? sort : defaultSort;
}

export const downloadAttachments = async (csrf, type, ids): Object => {
  let response = await postRequest({
    url: '/api/v2/attachments',
    data: { object_type: type, object_ids: ids },
    headers: { 'X-CSRF-Token': csrf },
    responseType: 'arraybuffer',
    resolver: response => response,
  });

  if (!response) {
    return { type: 'error', message: 'Oops, something went wrong.' };
  }

  // Expect JSON if backend returns error response
  if (response.headers['content-type'] == 'application/json') {
    let jsonResponse = JSON.parse(Buffer.from(response.data).toString('utf8'));
    if (jsonResponse && jsonResponse.type == 'error')
      return { type: jsonResponse.error_type, message: jsonResponse.msg };
  }

  // Expect file
  if (response.headers['content-type'] == 'application/zip') {
    try {
      const filename = filenameFromResponse(response);
      downloadFile(response.data, 'application/zip', filename);
      return { type: 'success' };
    } catch (error) {
      return { type: 'error', message: 'Oops, something went wrong.' };
    }
  }
};
